import type { MaterialsListData, MaterialsRow } from "@workspace/api-zod/materials-list";
import { materialMeasureUnit, materialPurchaseUnit, purchaseUnitLabel } from "./material-purchase-units";

export type OrderMaterial = {
  key: string;
  name: string;
  spec: string;
  unit: string;
  quantity: number;
  cost: number;
  category: string;
  priceVariants: number;
  contributors: { index: number; name: string }[];
  purchaseNote?: string;
  needsPackSize?: boolean;
};

const normalized = (value: string) => value.trim().replace(/\s+/g, " ").toLocaleLowerCase("en-GB");
const purchasingSpec = (value: string) => value.replace(/(?:\s*[;·]\s*)?(?:Cost Library|Estimated) buy price; confirm before ordering\.?$/i, "").trim();
const q4 = (value: number) => Math.round(value * 10_000) / 10_000;
const cents = (value: number) => Math.round(value * 100);

/** Consolidate physical products, not prices. Preserve every source cost in pence.
 * Optional work is kept separate so it cannot inflate the required order. */
export function consolidateOrderRows(rows: MaterialsRow[]): OrderMaterial[] {
  const grouped = new Map<string, OrderMaterial & { prices: Set<number>; costPence: number; purchase: ReturnType<typeof materialPurchaseUnit>; baseUnit: string }>();
  for (const row of rows) {
    const spec = purchasingSpec(row.spec);
    const purchase = materialPurchaseUnit({ ...row, spec });
    const key = JSON.stringify([normalized(row.name), normalized(spec), materialMeasureUnit(row.unit), purchase.unit, purchase.size]);
    const quantity = row.roundPacks ? row.roundedQuantity : row.orderQuantity ?? row.quantity;
    const current = grouped.get(key);
    if (current) {
      current.quantity = q4(current.quantity + quantity);
      current.costPence += cents(row.total);
      current.prices.add(row.unitPrice);
      for (const source of row.contributors) {
        if (!current.contributors.some(c => c.index === source.index)) current.contributors.push(source);
      }
    } else {
      grouped.set(key, {
        key, name: row.name, spec, unit: row.unit, quantity: q4(quantity),
        cost: 0, category: row.category, priceVariants: 1,
        contributors: [...row.contributors], prices: new Set([row.unitPrice]), costPence: cents(row.total),
        purchase, baseUnit: row.unit,
      });
    }
  }
  return [...grouped.values()].map(({ prices, costPence, purchase, baseUnit, ...row }) => {
    const units = row.quantity / purchase.size;
    // Round once after combining contributors, never one whole sheet per room.
    const quantity = purchase.whole ? Math.ceil(units - 1e-9) : row.quantity;
    const format = (n: number) => n.toLocaleString("en-GB", { maximumFractionDigits: 4 });
    return {
      ...row, unit: purchase.unit, quantity, priceVariants: prices.size, cost: costPence / 100,
      needsPackSize: purchase.missing,
      purchaseNote: purchase.missing
        ? "Purchase size or coverage is missing or ambiguous. Check before ordering."
        : purchase.size !== 1 || quantity !== row.quantity || purchase.unit !== materialMeasureUnit(baseUnit)
          ? `${format(row.quantity)} ${baseUnit} required · ${format(purchase.size)} ${baseUnit} per ${purchase.unit} · rounded up to whole ${purchaseUnitLabel(purchase.unit, 2)}.`
          : undefined,
    };
  }).sort((a, b) => a.name.localeCompare(b.name, "en-GB", { numeric: true }));
}

export function buildMaterialsOrder(data: MaterialsListData) {
  const included = consolidateOrderRows(data.included);
  const optional = consolidateOrderRows(data.optional);
  const total = (rows: OrderMaterial[]) => rows.reduce((sum, row) => sum + cents(row.cost), 0) / 100;
  return { included, optional, total: total(included), optionalTotal: total(optional),
    partial: !!(data.missing.length || data.pending.length || [...included, ...optional].some(row => row.needsPackSize)) };
}

/** Confirmation is bound to the actual purchase inputs, not quote selling prices. */
export function materialsConfirmationFingerprint(data: MaterialsListData): string {
  const input = JSON.stringify({
    purchaseUnitsVersion: 1,
    included: data.included.map(({ key, name, unit, packSize, spec, quantity, orderQuantity, roundedQuantity, roundPacks, unitPrice, total, contributors }) =>
      [key, name, unit, packSize, spec, quantity, orderQuantity, roundedQuantity, roundPacks, unitPrice, total, contributors]),
    optional: data.optional.map(({ key, name, unit, packSize, spec, quantity, orderQuantity, roundedQuantity, roundPacks, unitPrice, total, contributors }) =>
      [key, name, unit, packSize, spec, quantity, orderQuantity, roundedQuantity, roundPacks, unitPrice, total, contributors]),
    missing: data.missing, pending: data.pending,
  });
  let first = 2166136261, second = 2246822519;
  for (let i = 0; i < input.length; i++) {
    first = Math.imul(first ^ input.charCodeAt(i), 16777619);
    second = Math.imul(second ^ input.charCodeAt(i), 3266489917);
  }
  return `${input.length}-${(first >>> 0).toString(36)}-${(second >>> 0).toString(36)}`;
}
